import type {
	FieldPath,
	FieldValues,
	UseControllerProps,
} from 'react-hook-form'
import type { DataOption, GroupBase, OptionsOrGroups } from './types'

import React from 'react'
import { CircleHelpIcon, LoaderCircleIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import { isGrouped } from './utils'

export default function RHFSelect<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	OptionType extends DataOption = DataOption,
>({
	name,
	label,
	hint,
	description,
	defaultValue,
	shouldUnregister,
	options,
	placeholder = 'Select',
	className,
	disabled,
	isLoading,
	testId,
}: UseControllerProps<TFieldValues, TName> & {
	options: OptionsOrGroups<OptionType, GroupBase<OptionType>>
} & {
	placeholder?: string
	className?: string
	disabled?: boolean
	label?: string
	hint?: string
	description?: string
	isLoading?: boolean
	testId?: string
}) {
	const { control } = useFormContext()

	return (
		<FormField
			name={name}
			shouldUnregister={shouldUnregister}
			defaultValue={defaultValue}
			control={control}
			render={({ field, fieldState: { error } }) => (
				<FormItem>
					{!!label && (
						<FormLabel data-testid={testId ? `${testId}-label` : undefined}>
							{label}
							{hint && (
								<Tooltip>
									<TooltipTrigger asChild>
										<CircleHelpIcon className='size-3 text-muted-foreground' />
									</TooltipTrigger>
									<TooltipContent side='top'>{hint}</TooltipContent>
								</Tooltip>
							)}
						</FormLabel>
					)}
					<Select
						disabled={disabled}
						name={name}
						onValueChange={field.onChange}
						defaultValue={field.value}
						value={field.value}
					>
						<FormControl data-name={name}>
							<SelectTrigger
								data-testid={testId}
								ref={field.ref}
								aria-invalid={!!error}
								className={className}
							>
								<SelectValue className='w-full' placeholder={placeholder}>
									<div className='truncate'>
										{/* Children override Radix's own label, and Radix only falls
										    back to `placeholder` on an empty value — so an
										    out-of-catalog value would render an empty trigger that
										    reads as "unset". Show the raw value instead. */}
										{(isGrouped(options)
											? (options as GroupBase<OptionType>[])
													.flatMap((group) => group.options)
													.find((option) => option.value === field.value)?.label
											: (options as OptionType[]).find(
													(option) => option.value === field.value
												)?.label) ?? field.value}
									</div>
								</SelectValue>
							</SelectTrigger>
						</FormControl>
						<SelectContent>
							{isLoading ? (
								<div className='flex items-center justify-center py-4'>
									<LoaderCircleIcon className='size-4 animate-spin opacity-50' />
								</div>
							) : isGrouped(options) ? (
								(options as GroupBase<OptionType>[]).map((optionGroup) => (
									<SelectGroup key={optionGroup.label}>
										<SelectLabel>{optionGroup.label}</SelectLabel>
										{optionGroup.options?.map((option) => (
											<SelectItem
												key={option.value}
												value={option.value}
												data-value={option.value}
											>
												{option.label}
											</SelectItem>
										))}
									</SelectGroup>
								))
							) : (
								<SelectGroup>
									{(options as OptionType[]).map((option) => (
										<SelectItem
											key={option.value}
											value={option.value}
											data-value={option.value}
										>
											{option.description ? (
												<div className='flex flex-col gap-2'>
													<div>{option.label}</div>
													<div
														className='max-w-lg text-muted-foreground'
														dangerouslySetInnerHTML={{
															__html: option?.description ?? 'Description',
														}}
													/>
												</div>
											) : (
												option.label
											)}
										</SelectItem>
									))}
								</SelectGroup>
							)}
						</SelectContent>
					</Select>
					{!!description && <FormDescription>{description}</FormDescription>}
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
